import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Text } from '@cuemath/web-comps';
import { Separator } from '@cuemath/comps';

import Filters from './components/filters';
import Summary from './tabs/summary';
import PageAnalysis from './tabs/page-analysis';
import ExperimentAnalysis from './tabs/experiment-analysis';
import Category from './tabs/category';
import Blogs from './tabs/blogs';
import NonContent from './tabs/non-content';
import { useFunnelDataFreshness } from './api/get-funnel';
import { Root, HeaderRow, RefreshNote, TabBar, TabItem, InsightsLink, UI } from './styled-comps';
import { TABS, DEFAULT_TAB, BASE_PATH, COMPARE_MODES, CHANNELS } from './constants';
import { buildFunnelQuery, computeCompareWindow, hasValidWindow, formatRefreshedIST } from './helpers';

// Default window: current month-to-date — small, fast queries by default.
// Dates are normalized to midnight so two defaultFilters() calls serialize
// identically (draft vs applied equality drives the "Apply *" indicator).
const defaultFilters = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return {
    startDate: start,
    endDate: today,
    compareMode: COMPARE_MODES.NONE,
    compareStart: null,
    compareEnd: null,
    channels: [],
    regions: [],
    countries: [],
    userTypes: [],
    subSources: [],
  };
};

const WebsiteMetrics = () => {
  const { tab } = useParams();
  const history = useHistory();
  const activeTab = TABS.find(t => t.key === tab) || TABS.find(t => t.key === DEFAULT_TAB);

  // When the underlying Redshift funnel tables were last rebuilt (daily job).
  // Fetched once on mount — deliberately no deps, since @cuemath/rest-api `get`
  // is not referentially stable and would otherwise refire every render.
  const { data: freshData, get: getFreshness } = useFunnelDataFreshness();
  useEffect(() => {
    getFreshness('wm-freshness');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const refreshedLabel = formatRefreshedIST(freshData && freshData.last_refreshed_utc);

  // Draft = what the filter bar shows; applied = what actually drives queries.
  // Queries fire ONLY on Apply (or Reset / tab change), never per filter change.
  const [draft, setDraft] = useState(defaultFilters);
  const [applied, setApplied] = useState(defaultFilters);

  const handleDraftChange = useCallback(patch => {
    setDraft(prev => ({ ...prev, ...patch }));
  }, []);

  const handleApply = useCallback(() => {
    setApplied(draft);
  }, [draft]);

  const handleReset = useCallback(() => {
    const defaults = defaultFilters();
    setDraft(defaults);
    setApplied(defaults);
  }, []);

  const handleTabChange = key => {
    if (key !== activeTab.key) history.push(`${BASE_PATH}/${key}`);
  };

  // Clear ALL filters (back to month-to-date defaults) whenever the tab changes.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handleReset();
  }, [activeTab.key, handleReset]);

  const isPageAnalysis = activeTab.key === 'page-analysis';
  const isExperimentAnalysis = activeTab.key === 'experiment-analysis';
  const isBlogs = activeTab.key === 'blogs';
  const isSummary = !activeTab.channel && !isPageAnalysis && !isExperimentAnalysis;
  const isNonContent = activeTab.channel === CHANNELS.NON_CONTENT;

  // The shared filter bar renders only on Summary (date + comparison, region,
  // user type, category, sub-source). Every other tab manages its own controls.
  // Page and experiment now have their own dedicated tabs (Page Analysis /
  // Experiment Analysis), so they are no longer part of the Summary filter bar.
  const showFilterBar = isSummary;
  const showCategory = isSummary;
  const showSubSource = isSummary;

  const windowReady = hasValidWindow(applied);

  const primaryQuery = useMemo(
    () => buildFunnelQuery(applied, activeTab.channel, null),
    [applied, activeTab.channel],
  );

  const compareQuery = useMemo(() => {
    const window = computeCompareWindow(
      applied.startDate,
      applied.endDate,
      applied.compareMode,
      applied.compareStart,
      applied.compareEnd,
    );
    return window ? buildFunnelQuery(applied, activeTab.channel, window) : null;
  }, [applied, activeTab.channel]);

  const primaryKey = JSON.stringify(primaryQuery);
  const compareKey = compareQuery ? JSON.stringify(compareQuery) : '';
  const hasUnappliedChanges = JSON.stringify(draft) !== JSON.stringify(applied);

  const tabProps = {
    primaryQuery,
    compareQuery,
    primaryKey,
    compareKey,
  };

  return (
    <Root>
      <HeaderRow>
        <Text variant="h4" color={UI.ink}>Website Metrics</Text>
        <Text variant="body3" color={UI.muted}>
          Organic funnel · QL → Trial → Enrolled · source: mbt_leads + user_source_log
          {' · '}
          <InsightsLink
            href="https://static.cuemath.com/static/intel-admin/insights/website-organic-performance.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Insights snapshot ↗
          </InsightsLink>
        </Text>
      </HeaderRow>

      {refreshedLabel && (
        <>
          <Separator height={10} transparent />
          <RefreshNote>
            <Text variant="body3" color={UI.mutedDark}>
              Data last refreshed: {refreshedLabel}
            </Text>
          </RefreshNote>
        </>
      )}

      <Separator height={16} transparent />

      <TabBar>
        {TABS.map(t => (
          <TabItem key={t.key} $active={t.key === activeTab.key} onClick={() => handleTabChange(t.key)}>
            <Text variant="body2" color={t.key === activeTab.key ? UI.ink : UI.mutedDark}>
              {t.label}
            </Text>
          </TabItem>
        ))}
      </TabBar>

      {showFilterBar && (
        <>
          <Separator height={16} transparent />
          <Filters
            filters={draft}
            onChange={handleDraftChange}
            onApply={handleApply}
            onReset={handleReset}
            hasUnappliedChanges={hasUnappliedChanges}
            showCategory={showCategory}
            showSubSource={showSubSource}
          />
        </>
      )}

      {isPageAnalysis ? (
        <PageAnalysis key={activeTab.key} />
      ) : isExperimentAnalysis ? (
        <ExperimentAnalysis key={activeTab.key} />
      ) : isBlogs ? (
        <Blogs key={activeTab.key} />
      ) : isNonContent ? (
        <NonContent key={activeTab.key} channel={activeTab.channel} />
      ) : !showFilterBar ? (
        <Category key={activeTab.key} channel={activeTab.channel} />
      ) : !windowReady ? (
        <Text variant="body1" color={UI.muted}>Select a date range and press Apply to load metrics.</Text>
      ) : (
        <Summary {...tabProps} />
      )}
    </Root>
  );
};

export default WebsiteMetrics;
